import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { igrejasApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus,
  Church,
  Building2,
  MapPin,
  Phone,
  Mail,
  Edit,
  Loader2,
  Eye,
  EyeOff,
  User,
  Key
} from 'lucide-react';
import { toast } from 'sonner';

const tipoUnidadeOptions = [
  { value: 'sede', label: 'Sede', color: 'sky', nivel_required: 'super_admin' },
  { value: 'congregacao', label: 'Congregação', color: 'emerald', nivel_required: 'admin_igreja' },
  { value: 'subcongregacao', label: 'Subcongregação', color: 'violet', nivel_required: 'admin_congregacao' },
];

/** API pode enviar o enum com espaços/caso diferente; evita super_admin cair no ramo errado. */
const normalizeNivelAcesso = (nivel) => {
  if (nivel == null || typeof nivel !== 'string') return '';
  return nivel.trim().toLowerCase().replace(/\s+/g, '_');
};

export const Igrejas = () => {
  const { user } = useAuth();
  const [igrejas, setIgrejas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIgreja, setEditingIgreja] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  /** super_admin: true = fluxo "Nova sede" (tipo fixo, sem ambiguidade no select). */
  const [sedeOnlyFlow, setSedeOnlyFlow] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'sede',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone: '',
    email: '',
    sede_id: '',
    congregacao_id: '',
    admin_nome: '',
    admin_email: '',
    admin_password: '',
  });

  useEffect(() => {
    fetchIgrejas();
  }, []);

  // Definir tipo padrão quando o dialog abre
  useEffect(() => {
    if (dialogOpen && !editingIgreja) {
      const nivel = normalizeNivelAcesso(user?.nivel_acesso);
      if (sedeOnlyFlow) {
        setFormData((prev) => ({ ...prev, tipo: 'sede' }));
        return;
      }
      let defaultTipo = 'sede';
      if (nivel === 'admin_igreja') {
        defaultTipo = 'congregacao';
      } else if (nivel === 'admin_congregacao') {
        defaultTipo = 'subcongregacao';
      }
      setFormData((prev) => ({ ...prev, tipo: defaultTipo }));
    }
  }, [dialogOpen, editingIgreja, user?.nivel_acesso, sedeOnlyFlow]);

  const fetchIgrejas = async () => {
    try {
      const response = await igrejasApi.list();
      setIgrejas(response.data || []);
    } catch (error) {
      console.error('Error fetching igrejas:', error);
      toast.error('Erro ao carregar igrejas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!editingIgreja) {
      // Validar campos do admin apenas na criação
      if (!formData.admin_nome || !formData.admin_email || !formData.admin_password) {
        toast.error('Preencha todos os campos do administrador');
        return;
      }
      if (formData.admin_password.length < 6) {
        toast.error('A senha deve ter pelo menos 6 caracteres');
        return;
      }
      if (formData.tipo === 'congregacao') {
        const nivel = normalizeNivelAcesso(user?.nivel_acesso);
        if (nivel === 'super_admin' && !formData.sede_id) {
          toast.error('Selecione a sede vinculada à congregação');
          return;
        }
      }
      if (formData.tipo === 'subcongregacao') {
        const nivel = normalizeNivelAcesso(user?.nivel_acesso);
        if (
          (nivel === 'super_admin' || nivel === 'admin_igreja') &&
          !formData.congregacao_id
        ) {
          toast.error('Selecione a congregação vinculada');
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      if (editingIgreja) {
        // Na edição, não envia campos do admin
        const { admin_nome, admin_email, admin_password, ...updateData } = formData;
        await igrejasApi.update(editingIgreja.id, updateData);
        toast.success('Igreja atualizada com sucesso!');
      } else {
        const createPayload = { ...formData };
        if (createPayload.tipo === 'sede') {
          createPayload.sede_id = null;
          createPayload.congregacao_id = null;
        } else if (createPayload.tipo === 'congregacao') {
          createPayload.congregacao_id = null;
        }
        await igrejasApi.create(createPayload);
        toast.success(`Igreja criada! Credenciais: ${formData.admin_email}`);
      }
      setDialogOpen(false);
      resetForm();
      fetchIgrejas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar igreja');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'sede',
      endereco: '',
      cidade: '',
      estado: '',
      cep: '',
      telefone: '',
      email: '',
      sede_id: '',
      congregacao_id: '',
      admin_nome: '',
      admin_email: '',
      admin_password: '',
    });
    setEditingIgreja(null);
    setShowPassword(false);
    setSedeOnlyFlow(false);
  };

  const handleEdit = (igreja) => {
    setEditingIgreja(igreja);
    setFormData({
      nome: igreja.nome || '',
      tipo: igreja.tipo || 'sede',
      endereco: igreja.endereco || '',
      cidade: igreja.cidade || '',
      estado: igreja.estado || '',
      cep: igreja.cep || '',
      telefone: igreja.telefone || '',
      email: igreja.email || '',
      sede_id: igreja.sede_id || '',
      congregacao_id: igreja.congregacao_id || '',
    });
    setDialogOpen(true);
  };

  const getTipoConfig = (tipo) => {
    return tipoUnidadeOptions.find((t) => t.value === tipo) || tipoUnidadeOptions[0];
  };

  const sedes = igrejas.filter((i) => i.tipo === 'sede');
  const congregacoes = igrejas.filter((i) => i.tipo === 'congregacao');

  // Filtrar tipos disponíveis baseado no nível do usuário
  const getAvailableTipos = () => {
    const nivel = normalizeNivelAcesso(user?.nivel_acesso);
    if (nivel === 'super_admin') {
      return tipoUnidadeOptions; // Pode criar todos os tipos
    } else if (nivel === 'admin_igreja') {
      return tipoUnidadeOptions.filter((t) => t.value === 'congregacao');
    } else if (nivel === 'admin_congregacao') {
      return tipoUnidadeOptions.filter((t) => t.value === 'subcongregacao');
    }
    return [];
  };

  const availableTipos = getAvailableTipos();
  const nivelNorm = normalizeNivelAcesso(user?.nivel_acesso);
  const isSuperAdmin = nivelNorm === 'super_admin';
  const canCreateIgreja = availableTipos.length > 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="igrejas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Igrejas</h1>
          <p className="page-subtitle">Gerencie as unidades da sua organização</p>
        </div>
        {canCreateIgreja && (
          <div className="flex flex-wrap gap-2">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            {isSuperAdmin ? (
              <>
                <Button
                  type="button"
                  className="btn-primary"
                  data-testid="add-sede-btn"
                  onClick={() => {
                    resetForm();
                    setSedeOnlyFlow(true);
                    setFormData((prev) => ({ ...prev, tipo: 'sede' }));
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nova sede
                </Button>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-sky-200"
                    data-testid="add-igreja-btn"
                    onClick={() => {
                      setSedeOnlyFlow(false);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Outra unidade
                  </Button>
                </DialogTrigger>
              </>
            ) : (
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-igreja-btn">
                <Plus className="mr-2 h-4 w-4" />
                {nivelNorm === 'admin_igreja' ? 'Nova Congregação' : 'Nova Subcongregação'}
              </Button>
            </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingIgreja
                    ? 'Editar Igreja'
                    : sedeOnlyFlow
                      ? 'Nova sede'
                      : 'Nova unidade'}
                </DialogTitle>
                <DialogDescription>
                  {editingIgreja
                    ? 'Atualize os dados da igreja'
                    : sedeOnlyFlow
                      ? 'Cadastre a sede (unidade mãe) e o administrador que gerirá esta igreja.'
                      : 'Cadastre uma nova unidade com credenciais de acesso'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome da Igreja *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="input-field mt-1"
                    placeholder="Nome da igreja"
                    data-testid="input-nome-igreja"
                  />
                </div>

                {!editingIgreja && !sedeOnlyFlow && availableTipos.length > 1 && (
                  <div>
                    <Label>Tipo de Unidade *</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                    >
                      <SelectTrigger className="input-field mt-1" data-testid="select-tipo-igreja">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTipos.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!editingIgreja && sedeOnlyFlow && (
                  <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2 text-sm text-sky-900">
                    Tipo: <strong>Sede</strong> (unidade mãe, sem vínculo a outra igreja)
                  </div>
                )}

                {/* Super Admin criando congregação - precisa selecionar sede */}
                {isSuperAdmin && formData.tipo === 'congregacao' && (
                  <div>
                    <Label>Sede Vinculada *</Label>
                    {sedes.length === 0 ? (
                      <p className="mt-1 text-sm text-amber-700">
                        Cadastre uma sede antes (use o botão &quot;Nova sede&quot;).
                      </p>
                    ) : (
                      <Select
                        value={formData.sede_id}
                        onValueChange={(v) => setFormData({ ...formData, sede_id: v })}
                      >
                        <SelectTrigger className="input-field mt-1" data-testid="select-sede">
                          <SelectValue placeholder="Selecione a sede" />
                        </SelectTrigger>
                        <SelectContent>
                          {sedes.map((sede) => (
                            <SelectItem key={sede.id} value={sede.id}>{sede.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Super Admin ou Admin Igreja criando subcongregação - precisa selecionar congregação */}
                {['super_admin', 'admin_igreja'].includes(nivelNorm) && formData.tipo === 'subcongregacao' && congregacoes.length > 0 && (
                  <div>
                    <Label>Congregação Vinculada *</Label>
                    <Select
                      value={formData.congregacao_id}
                      onValueChange={(v) => setFormData({ ...formData, congregacao_id: v })}
                    >
                      <SelectTrigger className="input-field mt-1" data-testid="select-congregacao">
                        <SelectValue placeholder="Selecione a congregação" />
                      </SelectTrigger>
                      <SelectContent>
                        {congregacoes.map((cong) => (
                          <SelectItem key={cong.id} value={cong.id}>{cong.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    className="input-field mt-1"
                    data-testid="input-cidade-igreja"
                  />
                </div>
                <div>
                  <Label htmlFor="estado">Estado</Label>
                  <Input
                    id="estado"
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="input-field mt-1"
                    maxLength={2}
                    placeholder="UF"
                    data-testid="input-estado-igreja"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="endereco">Endereço</Label>
                <Input
                  id="endereco"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  className="input-field mt-1"
                  data-testid="input-endereco-igreja"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="input-field mt-1"
                    data-testid="input-telefone-igreja"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mail da Igreja</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field mt-1"
                    data-testid="input-email-igreja"
                  />
                </div>
              </div>

              {/* Campos do Administrador - apenas na criação */}
              {!editingIgreja && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-sky-500" />
                    Credenciais do Administrador
                  </h4>
                  <p className="text-sm text-slate-500 mb-4">
                    Estas credenciais serão usadas para acessar o sistema
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="admin_nome">Nome do Administrador *</Label>
                      <Input
                        id="admin_nome"
                        value={formData.admin_nome}
                        onChange={(e) => setFormData({ ...formData, admin_nome: e.target.value })}
                        className="input-field mt-1"
                        placeholder="Nome completo"
                        data-testid="input-admin-nome"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="admin_email">E-mail de Acesso *</Label>
                      <Input
                        id="admin_email"
                        type="email"
                        value={formData.admin_email}
                        onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                        className="input-field mt-1"
                        placeholder="email@igreja.com"
                        data-testid="input-admin-email"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="admin_password">Senha de Acesso *</Label>
                      <div className="relative">
                        <Input
                          id="admin_password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.admin_password}
                          onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                          className="input-field mt-1 pr-10"
                          placeholder="Mínimo 6 caracteres"
                          data-testid="input-admin-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 mt-0.5"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-igreja">
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    editingIgreja ? 'Atualizar' : 'Criar'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-sky-50">
              <Church className="w-5 h-5 text-sky-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Sedes</p>
              <p className="text-2xl font-bold text-slate-900">{sedes.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-50">
              <Building2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Congregações</p>
              <p className="text-2xl font-bold text-slate-900">{congregacoes.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-violet-50">
              <MapPin className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Subcongregações</p>
              <p className="text-2xl font-bold text-slate-900">
                {igrejas.filter((i) => i.tipo === 'subcongregacao').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Churches List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [1, 2, 3].map((i) => (
            <Card key={i} className="stat-card animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-slate-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))
        ) : igrejas.length === 0 ? (
          <Card className="col-span-full stat-card">
            <CardContent className="py-12 text-center">
              <Church className="mx-auto h-12 w-12 text-slate-300" />
              <h3 className="mt-4 text-lg font-medium text-slate-900">Nenhuma igreja cadastrada</h3>
              <p className="mt-2 text-slate-500">Comece cadastrando sua primeira igreja.</p>
            </CardContent>
          </Card>
        ) : (
          igrejas.map((igreja) => {
            const tipoConfig = getTipoConfig(igreja.tipo);
            return (
              <Card key={igreja.id} className="stat-card card-hover" data-testid={`igreja-card-${igreja.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-2 rounded-lg bg-${tipoConfig.color}-50`}>
                      <Church className={`w-5 h-5 text-${tipoConfig.color}-500`} />
                    </div>
                    <Badge variant="secondary" className={`bg-${tipoConfig.color}-50 text-${tipoConfig.color}-700`}>
                      {tipoConfig.label}
                    </Badge>
                  </div>

                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{igreja.nome}</h3>

                  <div className="space-y-2 text-sm text-slate-500">
                    {igreja.cidade && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{igreja.cidade}{igreja.estado ? ` - ${igreja.estado}` : ''}</span>
                      </div>
                    )}
                    {igreja.telefone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        <span>{igreja.telefone}</span>
                      </div>
                    )}
                    {igreja.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{igreja.email}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => handleEdit(igreja)}
                    data-testid={`edit-igreja-${igreja.id}`}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </Button>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Igrejas;
